import { z } from "zod";

import { pendingCoverageContextSchema } from "@/shared/scheduling/coverage-context";
import { phoneNumberSchema } from "@/shared/utils/phone-utils";

export enum IntakeSmsAction {
  Prepare = "prepare",
  Send = "send",
}

export enum IntakeSmsResultStatus {
  NotNeeded = "not_needed",
  Queued = "queued",
  StaffQueued = "staff_queued",
  Ready = "ready",
  Sent = "sent",
  Unavailable = "unavailable",
  Unknown = "unknown",
}

export enum IntakeSmsConsentSource {
  Athena = "athena",
  Voice = "voice",
}

export enum IntakeSmsFallbackReason {
  Declined = "declined",
  Unavailable = "unavailable",
}

export const voiceIntakeBookingSchema = z.object({
  intakeCallStartedAt: z.string().datetime().optional(),
  intakeSmsFallback: z.enum(IntakeSmsFallbackReason).optional(),
});

export enum IntakeSmsUnavailableReason {
  ActiveConversation = "active_conversation",
  MissingPhone = "missing_phone",
  OptedOut = "opted_out",
  PhoneChanged = "phone_changed",
  SendFailed = "send_failed",
  Unsupported = "unsupported",
  ConsentNotOnFile = "consent_not_on_file",
}

const requestContext = z.object({
  groupId: z.string().uuid(),
  patientId: z.string().uuid(),
  requestId: z.string().min(1).max(200),
});

export const intakeSmsConsentSchema = z.object({
  consent: z.literal(true),
  consentSource: z.enum(IntakeSmsConsentSource).optional(),
  phoneNumber: phoneNumberSchema,
});

export const intakeSmsRequestSchema = z.discriminatedUnion("action", [
  requestContext.extend({
    action: z.literal(IntakeSmsAction.Prepare),
    practitionerId: z.string().uuid().optional(),
    locationId: z.string().uuid().optional(),
    appointmentDate: z.string().datetime({ offset: true }).optional(),
    pendingCoverage: pendingCoverageContextSchema.optional(),
  }),
  requestContext.extend({
    action: z.literal(IntakeSmsAction.Send),
    ...intakeSmsConsentSchema.shape,
  }),
]);

export const intakeSmsResponseSchema = z.discriminatedUnion("status", [
  z.object({ status: z.literal(IntakeSmsResultStatus.NotNeeded) }),
  z.object({
    status: z.literal(IntakeSmsResultStatus.Ready),
    phoneNumber: phoneNumberSchema,
    bookingHandoffSupported: z.literal(true).optional(),
    requestedInformation: z.array(z.string()).optional(),
    consentSource: z.enum(IntakeSmsConsentSource).optional(),
  }),
  z.object({ status: z.literal(IntakeSmsResultStatus.Queued) }),
  z.object({ status: z.literal(IntakeSmsResultStatus.StaffQueued) }),
  z.object({ status: z.literal(IntakeSmsResultStatus.Unknown) }),
  z.object({ status: z.literal(IntakeSmsResultStatus.Sent), messageId: z.string().uuid() }),
  z.object({
    status: z.literal(IntakeSmsResultStatus.Unavailable),
    reason: z.enum(IntakeSmsUnavailableReason),
  }),
]);

export type IntakeSmsRequest = z.infer<typeof intakeSmsRequestSchema>;
export type IntakeSmsResponse = z.infer<typeof intakeSmsResponseSchema>;
